import { Typography } from '@mui/material';
import { SimpleTreeView } from '@mui/x-tree-view';
import type { StatusesByField } from 'business/types';
import { type CatalogSelection } from 'modules/datacatalog/types';
import type { TreeData } from 'modules/decision/pages/Explore/components/Sentence/helpers/treeData';
import type { SyntheticEvent } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import DomainRenderer from './components/DomainRenderer';

const DOMAINS_AT_END = ['global', 'CSV', 'VIEW', 'unclassified'];

type Props = {
    treeData: TreeData;
    isSearchUsed: boolean;
    searchQuery?: string;
    selectedFunctions: string[];
    selection: CatalogSelection | null;
    onSelect: (selection: CatalogSelection) => void;
    expanded: string[];
    setExpanded: (event: SyntheticEvent, nodeIds: string[]) => void;
    displayedStatuses?: StatusesByField;
};

const CatalogTree = ({
    treeData,
    isSearchUsed,
    searchQuery,
    selectedFunctions,
    selection,
    onSelect,
    expanded,
    setExpanded,
    displayedStatuses,
}: Props) => {
    const { t } = useTranslation();

    const selectedName = selection?.name ?? null;

    const visibleDomains = useMemo(
        () =>
            Object.keys(treeData)
                .filter((domain) => {
                    if (DOMAINS_AT_END.includes(domain)) return true;
                    if (selectedFunctions.length === 0) return true;
                    return selectedFunctions.includes(domain);
                })
                .map((domain) => ({
                    value: domain,
                    label: t(`dataCatalog.${domain.toLowerCase()}`),
                }))
                .sort((a, b) => {
                    const aEnd = DOMAINS_AT_END.includes(a.value);
                    const bEnd = DOMAINS_AT_END.includes(b.value);
                    if (aEnd && bEnd)
                        return (
                            DOMAINS_AT_END.indexOf(a.value) -
                            DOMAINS_AT_END.indexOf(b.value)
                        );
                    if (aEnd) return 1;
                    if (bEnd) return -1;
                    return a.label.localeCompare(b.label);
                }),
        [treeData, selectedFunctions, t],
    );

    if (visibleDomains.length === 0) {
        return (
            <Typography sx={{ p: 3, color: 'text.secondary' }}>
                {isSearchUsed
                    ? t('explore.noFieldMatch')
                    : t('explore.noFields')}
            </Typography>
        );
    }

    return (
        <SimpleTreeView
            expandedItems={expanded}
            onExpandedItemsChange={setExpanded}
            sx={{ width: '100%', overflow: 'auto', pb: 3 }}
        >
            {visibleDomains.map((domain) => (
                <DomainRenderer
                    key={domain.value}
                    domain={domain}
                    filteredTreeData={treeData}
                    selectedName={selectedName}
                    onSelect={onSelect}
                    searchQuery={searchQuery}
                    displayedStatuses={displayedStatuses}
                />
            ))}
        </SimpleTreeView>
    );
};

export default CatalogTree;
